import { useState, useCallback } from "react";
import { useAuth } from "react-oidc-context";
import { apiService } from "../services/api";
import { AuthService } from "../services/auth";
import { CACHE_CONFIG } from "../config/constants";
import { CachedVideoUrl, ApiError } from "../types/api";

interface UseVideoUrlReturn {
  fetchVideoUrl: (videoKey: string) => Promise<string | null>;
  isLoading: boolean;
  error: ApiError | null;
}

// In-memory cache for video URLs with expiration
const videoUrlCache = new Map<string, CachedVideoUrl>();

export function useVideoUrl(): UseVideoUrlReturn {
  const auth = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchVideoUrl = useCallback(
    async (videoKey: string): Promise<string | null> => {
      if (!AuthService.isAuthenticated(auth)) {
        setError({ message: "User is not authenticated" });
        return null;
      }

      // Check cache first
      const cached = videoUrlCache.get(videoKey);
      const now = Date.now();

      if (cached && now < cached.expiresAt) {
        return cached.url;
      }

      try {
        setIsLoading(true);
        setError(null);

        const idToken = AuthService.getIdToken(auth);
        if (!idToken) {
          throw new Error("No authentication token available");
        }
        const response = await apiService.getVideoUrl(videoKey, idToken);

        // Cache the URL with expiration
        const cachedUrl: CachedVideoUrl = {
          url: response.url,
          timestamp: now,
          expiresAt: now + CACHE_CONFIG.videoUrlTtl,
        };
        videoUrlCache.set(videoKey, cachedUrl);

        return response.url;
      } catch (err) {
        const apiError: ApiError = {
          message:
            err instanceof Error ? err.message : "Failed to fetch video URL",
          status: (err as ApiError)?.status,
          code: (err as ApiError)?.code,
        };
        setError(apiError);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [auth],
  );

  return {
    fetchVideoUrl,
    isLoading,
    error,
  };
}
